import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthField, AuthShell, FormError } from "@/components/AuthForm";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";
import { GoogleButton } from "@/components/GoogleAuth";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from || "/resumen", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Inicia sesión" subtitle="Bienvenido de vuelta. Retoma el control de tu dinero.">
      <GoogleButton back="/login" testId="login-google-button" />
      <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-[#DDE1E8]" />o con tu correo<span className="h-px flex-1 bg-[#DDE1E8]" /></div>
      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <AuthField id="login-email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
        <AuthField id="login-password" label="Contraseña" type="password" value={password} onChange={setPassword}
          autoComplete="current-password" extra={<ForgotPasswordDialog />} />
        <FormError message={error} testId="login-error" />
        <Button type="submit" disabled={loading} className="h-11 w-full bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="login-submit-button">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        ¿Aún no tienes cuenta?{" "}
        <Link to="/registro" className="font-semibold text-[#1B2A4A] underline-offset-4 hover:underline" data-testid="go-to-register-link">Regístrate</Link>
      </p>
    </AuthShell>
  );
}
